import { useEffect } from 'react';
import { getEventId } from './fueled.utils';
import { useScriptLoad } from './hooks';
import { useAnalytics } from './FueledAnalytics';
import { TRACKING_EVENTS } from './fueled.events';

const EVENTS = [
  TRACKING_EVENTS.VIEW_ITEM,
  TRACKING_EVENTS.VIEW_ITEM_LIST,
  TRACKING_EVENTS.ADD_TO_CART,
  TRACKING_EVENTS.SIGN_UP,
  TRACKING_EVENTS.VIEW_SEARCH_RESULTS,
];

export function FueledTikTokScript({ withKey: tiktokPixelId }) {
  const { register, subscribe } = useAnalytics();
  const ready = register('tiktok-script');

  const { status } = useScriptLoad(
    {
      id: 'tiktok-script',
      innerHTML: `
        !function (w, d, t) { w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
        ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],
        ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
        for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
        ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},
        ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";
        ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};
        var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;
        var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)}; ttq.load('${tiktokPixelId}'); ttq.instance('${tiktokPixelId}').page(); }(window, document, 'ttq');
      `,
      waitFor: () => !!window?.ttq,
    },
    'head',
    Boolean(tiktokPixelId)
  );

  useEffect(() => {
    if (status !== 'ready') return;

    ready();
  }, [status]);

  useEffect(() => {
    const identify = (event) => {
      if (event.detail.user_properties?.customer_email) {
        window.ttq.identify({
          external_id: event.detail.user_properties?.customer_id,
          email: event.detail.user_properties?.customer_email,
        });
      }
    };

    const viewItemListener = (event) => {
      const eventId = getEventId(event);
      const [product] = event.detail.ecommerce.detail.products;

      window.ttq.instance(tiktokPixelId).track(
        'ViewContent',
        {
          content_name: product.name,
          content_category: product.category,
          content_id: product.product_id,
          content_type: 'product_group',
          price: product.price,
          value: product.price,
          currency: event.detail.ecommerce.currency_code,
        },
        eventId ? { event_id: eventId } : undefined
      );
    };

    const addToCartListener = (event) => {
      const eventId = getEventId(event);
      const [product] = event.detail.ecommerce.add.products;

      window.ttq.instance(tiktokPixelId).track(
        'AddToCart',
        {
          content_name: product.name,
          content_category: product.category,
          content_id: product.product_id,
          content_type: 'product_group',
          quantity: product.quantity,
          price: product.price,
          value: product.price * product.quantity,
          currency: event.detail.ecommerce.currency_code,
        },
        eventId ? { event_id: eventId } : undefined
      );
    };
    const viewSearchResultsListener = (event) => {
      try {
        const eventId = getEventId(event);

        const searchTerm = event?.detail?.ecommerce?.actionField?.search_term;

        if (searchTerm) {
          window.ttq.instance(tiktokPixelId).track(
            'Search',
            {
              query: searchTerm,
            },
            eventId ? { event_id: eventId } : undefined
          );
        }
      } catch (e) {
        console.log(e);
      }
    };

    const signUpListener = (event) => {
      try {
        window.ttq.instance(tiktokPixelId).track('CompleteRegistration', {});
      } catch (e) {
        console.log(e);
      }
    };

    const viewItemListListener = (event) => {
      try {
        const eventId = getEventId(event);
        const collectionProducts = event?.detail?.ecommerce?.products;

        const contentProducts = collectionProducts.map(function (product) {
          return {
            content_name: product.name,
            content_category: product.category,
            content_id: product.product_id,
            content_type: 'product_group',
            price: product.price,
          };
        });

        if (collectionProducts) {
          window.ttq.instance(tiktokPixelId).track(
            'ViewContent',
            {
              contents: contentProducts,
              content_type: 'product_group',
              currency: event?.detail?.ecommerce?.currency_code,
              value:
                collectionProducts?.reduce((acc, item) => {
                  return acc + parseFloat(item.price);
                }, 0) || [],
            },
            eventId ? { event_id: eventId } : undefined
          );
        }
      } catch (e) {
        console.log(e);
      }
    };

    const eventHandler = (event) => {
      identify(event);

      switch (event.type) {
        case TRACKING_EVENTS.VIEW_ITEM:
          viewItemListener(event);
          break;
        case TRACKING_EVENTS.VIEW_ITEM_LIST:
          viewItemListListener(event);
          break;
        case TRACKING_EVENTS.ADD_TO_CART:
          addToCartListener(event);
          break;
        case TRACKING_EVENTS.SIGN_UP:
          signUpListener(event);
          break;
        case TRACKING_EVENTS.VIEW_SEARCH_RESULTS:
          viewSearchResultsListener(event);
          break;
        default:
          break;
      }
    };
    const unsubscribeArray = EVENTS.map((eventType) =>
      subscribe(eventType, eventHandler)
    );

    return () => {
      unsubscribeArray.forEach((unsubscribe) => unsubscribe());
    };
  }, []);

  return null;
}
